import NotificationPanel from "./NotificationPanel";
import { useUsersOrders } from "@/queries/order/useUsersOrders";
import { usePersonalAllocations } from "@/queries/allocations/usePersonalAllocations";
import { usePowerGenerationInfo } from "@/queries/user/usePowerGenerationInfo";
import { useUser } from "@/queries/user/useUser";
import { useLinkCounts } from "@/hooks/useLinkCounts";
import BuyMorePromoCard from "./redesign/BuyMorePromoCard";
import DashboardHeader from "./redesign/DashboardHeader";
import DisbursementsChartCard from "./redesign/DisbursementsChartCard";
import LinkedAccountsCard from "./redesign/LinkedAccountsCard";
import MetricCardsRow from "./redesign/MetricCardsRow";
import OwnershipSummaryCard from "./redesign/OwnershipSummaryCard";
import RecentPurchasesCard from "./redesign/RecentPurchasesCard";
import { useTrackerSummary } from "@/queries/tracker/useTrackerSummary";

export default function DashboardPanel() {
  const {
    data: user,
    error: userError,
    isLoading: userLoading,
    refetch: refetchUser,
  } = useUser();
  const {
    data: powerGenerationInfo,
    error: powerGenerationError,
    isLoading: powerGenerationLoading,
    refetch: refetchPowerGenerationInfo,
  } = usePowerGenerationInfo({ queryConfig: { enabled: !!user } });
  const {
    data: usersOrders,
    error: usersOrdersError,
    isLoading: usersOrdersLoading,
    refetch: refetchUsersOrders,
  } = useUsersOrders({ queryConfig: { enabled: !!user } });
  const {
    data: trackerSummary,
    error: trackerSummaryError,
    isPending: trackerSummaryPending,
    refetch: refetchTrackerSummary,
  } = useTrackerSummary();

  const {
    data: allocations,
    isLoading: allocationsLoading,
    error: allocationsError,
    refetch: refetchAllocations,
  } = usePersonalAllocations();
  const { pendingCapacity } = useLinkCounts();
  const pendingCapacityWatts =
    Array.isArray(user?.orderSummaries) ? pendingCapacity : undefined;

  return (
    <div className="min-h-[calc(100vh-200px)] w-full space-y-8 bg-[#080A0E] py-4">
      <div className="mx-auto w-full max-w-7xl space-y-6">
        <DashboardHeader givenName={user?.givenName} />
        <NotificationPanel />
        <MetricCardsRow />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(260px,1fr)] lg:items-stretch">
          <div className="min-w-0">
            <DisbursementsChartCard />
          </div>
          <div className="min-w-0">
            <BuyMorePromoCard />
          </div>
          <div className="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-2">
            <OwnershipSummaryCard
              activeWatts={powerGenerationInfo?.user?.active}
              pendingCapacityWatts={pendingCapacityWatts}
              orders={usersOrders}
              trackerSummary={trackerSummary}
              isUserLoading={userLoading}
              isPowerGenerationLoading={powerGenerationLoading}
              isOrdersLoading={usersOrdersLoading}
              isTrackerSummaryPending={trackerSummaryPending}
              userError={userError}
              powerGenerationError={powerGenerationError}
              ordersError={usersOrdersError}
              trackerSummaryError={trackerSummaryError}
              onRetry={() => {
                void Promise.all([
                  refetchUser(),
                  refetchPowerGenerationInfo(),
                  refetchUsersOrders(),
                  refetchTrackerSummary(),
                ]);
              }}
            />
            <RecentPurchasesCard
              orders={usersOrders}
              isLoading={usersOrdersLoading}
              error={usersOrdersError}
              onRetry={() => void refetchUsersOrders()}
            />
          </div>
          <div className="min-w-0">
            <LinkedAccountsCard
              allocations={allocations}
              isLoading={allocationsLoading}
              error={allocationsError}
              onRetry={() => {
                void refetchAllocations();
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
